import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCAL_LANGUAGES } from '@rd-web-markets/market/dist/constants';

/**
 * Loads translations (+ their tooltips, into a parallel 'tooltips' i18next namespace)
 * for the given user, if their account_type is in allowedAccountTypes.
 *
 * @param {*} user - the logged-in user object (or null)
 * @param {string|string[]} allowedAccountTypes - account_type(s) this route should load translations for
 * @param {*} fetchService - translationsService or listsTranslationsService (must expose .all(locale))
 * @returns {Array|null} the raw keyValues once loaded, or null before that
 */
export const useLoadTranslationsWithTooltips = (user, allowedAccountTypes, fetchService) => {
  const { i18n } = useTranslation();
  const defaultLanguage = LOCAL_LANGUAGES[0];
  const [translations, setTranslations] = useState(null);

  const allowedTypes = Array.isArray(allowedAccountTypes) ? allowedAccountTypes : [allowedAccountTypes];
  const isAllowed = !!user && allowedTypes.includes(user.account_type);

  useEffect(() => {
    const fetchTranslations = async () => {
      if (!isAllowed) return;

      const locale = user?.local_language || defaultLanguage;
      const keyValues = await fetchService.all(locale);
      const englishValues = locale !== 'en' ? await fetchService.all('en') : keyValues;

      const json = {};
      const englishJson = {};
      const tooltipJson = {};
      const englishTooltipJson = {};

      englishValues.forEach((englishValue) => {
        englishJson[englishValue.key] = englishValue.value;
        if (englishValue.tooltip) englishTooltipJson[englishValue.key] = englishValue.tooltip;
      });
      keyValues.forEach((keyValue) => {
        json[keyValue.key] = keyValue.value;
        if (keyValue.tooltip) tooltipJson[keyValue.key] = keyValue.tooltip;
      });

      i18n.addResourceBundle('en', 'translation', englishJson, true, true);
      i18n.addResourceBundle('en', 'tooltips', englishTooltipJson, true, true);
      i18n.addResourceBundle(locale, 'translation', json, true, true);
      i18n.addResourceBundle(locale, 'tooltips', tooltipJson, true, true);
      i18n.changeLanguage(locale);
      setTranslations(keyValues);
    };
    fetchTranslations();
  }, []);

  return translations;
};